import { useEffect, useMemo, useState } from 'react';
import UserContext, { AuthProps, BEARER_REGEX, K_USER_KEY } from 'context/user';
import openRegistrationScreen from 'sdk/actionCreators/openRegistrationScreen';
import { getContext } from '@picsart/miniapps-sdk';
import { TierPlans } from '@picsart/cloud-storage/configs';
import type { IProvider } from 'providers/types';
import { API_URL } from 'config/api';
import { IUserInfo } from '@picsart/miniapp-sdk-types';
import shallowEqual from 'utils/shallowEqual';

const getTier = async (): Promise<any> => {
  const resp = await getContext().utils.api.fetch(`${API_URL}/guard/users/tier`);

  return resp.json();
};

function UserProvider({ children }: IProvider) {
  const [user, setUser] = useState<IUserInfo>(getContext().general.auth.getState());
  const [auth, setAuth] = useState<AuthProps>({ accessToken: '', key: '' });
  const [tier, setTier] = useState<TierPlans>(TierPlans.FREE);

  useEffect(() => {
    getContext().handlers.onHeadersUpdate(headers => {
      setAuth({
        key: headers[K_USER_KEY],
        accessToken: headers.Authorization?.replace(BEARER_REGEX, ''),
      });
    });

    const getTierInfo = async () => {
      try {
        const { response } = await getTier();
        setTier(response.id);
      } catch {
        setTier(TierPlans.FREE);
      }
    };

    const subscribe = (nextValue: IUserInfo) => {
      getTierInfo();
      setUser(prevUser => {
        if (nextValue && !shallowEqual(nextValue, prevUser)) {
          return nextValue;
        }
        return prevUser;
      });
    };

    getTierInfo();
    return getContext().general.auth.subscribe(subscribe, { needPrevious: true });
  }, []);

  useEffect(() => {
    if (Object.prototype.hasOwnProperty.call(user, 'isAuthorized')) {
      const headers = getContext().utils.api.getHeaders();
      if (headers && headers?.Authorization) {
        setAuth({
          key: headers[K_USER_KEY],
          accessToken: headers.Authorization?.replace(BEARER_REGEX, ''),
        });
      } else {
        getContext().handlers.publishEvent(openRegistrationScreen());
      }
    }
  }, [user]);

  const value = useMemo(
    () => ({
      ...user,
      ...auth,
      userTier: {
        id: tier,
      },
    }),
    [auth, tier, user],
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export default UserProvider;
